import React, { Component } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { Form } from "react-bootstrap";
import { Formik } from "formik";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import queryString from "query-string";
import { Navigate } from "react-router-dom";
import { get, includes, toUpper, trim } from "lodash";

import { registerUserRequest } from "actions/userActions";
import { getEmployerOrder } from "actions/employerActions";
import { userConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { emailValidator, testUsername } from "utils/fieldValidators";
import ProgressBar from "components/ProgressBar";
import CircularProgressBar from "components/CircularProgressBar";
import Button from "components/Button";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";

import "./EmployerEnroll.scss";
import { CREATE_PROFILE } from "statics/onboardingSteps";
import { ScrollToFieldError } from "utils/form";
import { wholesaleBillingReferralCodes } from "statics/payrollProviders";
import SignupLinkCard, {
  SalesEmailLink,
} from "pages/signUp/employer/SignupLinkCard";
import { openEmployerSignup } from "utils/openEmployerSignup";
import SignupSalesPanel from "./SignupSalesPanel";
import type { RootState } from "types/state";

import * as yup from "yup";

const schema = yup.object({
  username: yup
    .string()
    .label("Username")
    .required()
    .min(3, "Must be at least three characters")
    .max(25)
    // testUsername's `this` type ({ createError }) is disjoint from yup's
    // TestFunction signature; boundary-cast so its runtime behavior is kept.
    .test(
      "username",
      "Invalid username.",
      testUsername as unknown as yup.TestFunction<string | undefined>
    ),
  email: emailValidator("You must enter a valid email address")
    .label("Email")
    .max(40)
    .required(),
  firstName: yup.string().label("First Name").required(),
  lastName: yup.string().label("Last Name").required(),
  password: yup
    .string()
    .label("Password")
    .required()
    .min(8, "Seems a bit short...")
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
      "Must be at least 8 characters long with one uppercase, one lowercase, one number and one special character (e.g. #?!@$%^&*- )"
    ),
  confirmPassword: yup
    .string()
    .required()
    .label("Confirm password")
    .test("passwords-match", "Passwords must match", function (value) {
      return this.parent.password === value;
    }),
  referralCode: yup.string().label("Referral Code").max(30),
});

const isPartnerReferralCode = (referralCode: string) =>
  includes(wholesaleBillingReferralCodes, toUpper(trim(referralCode)));

const partnerSchema = schema.shape({
  referralCode: yup
    .string()
    .label("Referral Code")
    .required("Enter the referral code from your partner")
    .test("partner-referral-code", "This referral code is not valid", (value) =>
      isPartnerReferralCode(value || "")
    ),
});

const actions = [userConstants.REGISTER_USER];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isFetching: loadingSelector(state),
  };
};

const mapDispatchToProps = {
  registerUserRequest,
  getEmployerOrder,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  userId?: string;
  // the /partner route, for wholesale billing partners identified by their referral code
  partner?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface SignupFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  username: string;
  referralCode: string;
}

type SignupLinkStatus = "loading" | "valid" | "used" | "invalid";

interface State {
  user: {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    role: string;
    referralCode: string;
  };
  signupCode: string;
  signupLinkStatus: SignupLinkStatus | null;
  signupLinkError: string;
  submitted: boolean;
}

class EmployerSignup extends Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    const queryParams = queryString.parse(window.location.search);
    // query-string types params as string | string[] | null; this URL only
    // ever carries scalar values, narrow at the boundary (runtime no-op)
    const referralCode = get(queryParams, "referralCode", "") as string;
    const signupCode = toUpper(
      trim(get(queryParams, "signupCode", "") as string)
    );

    this.state = {
      user: {
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        role: "",
        referralCode,
      },
      signupCode,
      signupLinkStatus: signupCode && !props.partner ? "loading" : null,
      signupLinkError: "",
      submitted: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    window.analytics.page("Employer Signup");
    if (this.state.signupLinkStatus === "loading") {
      void this._checkSignupLink();
    }
  }

  _checkSignupLink = async () => {
    const result = (await this.props.getEmployerOrder(
      this.props.client as ApolloClient<object>,
      this.state.signupCode
    )) as {
      type: string;
      success: boolean;
      data?: { isLinked: boolean };
      error?: string;
    };
    if (!this._isMounted) return;

    if (result.success) {
      this.setState({
        signupLinkStatus: result.data?.isLinked ? "used" : "valid",
      });
    } else {
      this.setState({
        signupLinkStatus: "invalid",
        signupLinkError: result.error || "",
      });
    }
  };

  _renderSignupLinkMessage = () => {
    const { signupLinkStatus, signupLinkError } = this.state;

    if (signupLinkStatus === "used") {
      return (
        <SignupLinkCard headerText="Signup Link Already Used">
          A company has already been created with this signup link. If that was
          you, please <a href="/login">log in</a>. Otherwise contact{" "}
          <SalesEmailLink />.
        </SignupLinkCard>
      );
    }

    if (signupLinkStatus === "invalid") {
      return (
        <SignupLinkCard headerText="Invalid Signup Link">
          {signupLinkError ||
            "We couldn't verify this signup link. Please try again."}{" "}
          If the problem continues, contact <SalesEmailLink />.
        </SignupLinkCard>
      );
    }

    return <SignupSalesPanel />;
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  // signup without a link or partner route, only where OPEN_EMPLOYER_SIGNUP is set (staging)
  _isOpenSignup = () =>
    !this.props.partner && !this.state.signupCode && openEmployerSignup();

  submitUser = (values: SignupFormValues) => {
    let user = {
      username: values.username,
      email: values.email,
      password: values.password,
      firstName: trim(values.firstName),
      lastName: trim(values.lastName),
      role: "Employer Admin",
      referralCode: this.props.partner
        ? toUpper(trim(values.referralCode))
        : this._isOpenSignup()
          ? trim(values.referralCode) || null
          : null,
      signupCode: this.props.partner ? null : this.state.signupCode || null,
    };
    this.props.registerUserRequest(
      this.props.client as ApolloClient<object>,
      user
    );

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  render() {
    var articleStyle = {
      paddingBottom: 0,
    };

    const { partner } = this.props;
    const { signupCode, signupLinkStatus } = this.state;
    const urlReferralCode = this.state.user.referralCode;

    if (!partner && !signupCode) {
      // links handed out before partner routes existed carry the partner code on the root route
      if (isPartnerReferralCode(urlReferralCode)) {
        return (
          <Navigate
            to={`/partner?${queryString.stringify({
              referralCode: urlReferralCode,
            })}`}
            replace
          />
        );
      }
      if (!openEmployerSignup()) {
        return (
          <div className="mega-container">
            {this._renderSignupLinkMessage()}
          </div>
        );
      }
    }

    if (signupLinkStatus === "loading") {
      return <IconSpinner centered />;
    }

    if (signupLinkStatus === "used" || signupLinkStatus === "invalid") {
      return (
        <div className="mega-container">{this._renderSignupLinkMessage()}</div>
      );
    }

    const isOpenSignup = this._isOpenSignup();
    // a partner code from the link is locked in, an open signup locks whatever code the link carried
    const lockReferralCode = partner
      ? isPartnerReferralCode(urlReferralCode)
      : !!urlReferralCode;

    return (
      <div>
        <ProgressBar
          isEmployer={true}
          showBillingRoute={false}
          activeStepId={CREATE_PROFILE.id}
          progressPercent={"25"}
        />
        <Formik
          validateOnChange={false}
          validationSchema={partner ? partnerSchema : schema}
          onSubmit={(values) => {
            this.submitUser(values);
          }}
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
            username: "",
            referralCode: this.state.user.referralCode,
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="25"
                >
                  <section className="page-title-wrap">
                    <article className="text-cell">
                      <p className="page-title">Create Your Company Account</p>
                      <p className="page-subtext">
                        Let&#39;s start by creating your company administrator
                        login.
                      </p>
                    </article>
                    <article className="progress-cell">
                      <ul className="circular-progress-wrap">
                        <CircularProgressBar
                          strokeWidth="8"
                          sqSize="75"
                          percentage="25"
                        />
                      </ul>
                    </article>
                  </section>
                  <div id="form-employer-profile">
                    <section className="form-sec-2col">
                      <article className="col-form" style={articleStyle}>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicUsername"
                        >
                          <Form.Control
                            name="username"
                            placeholder="Username"
                            value={values.username}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.username && !!errors.username}
                            isValid={touched.username && !errors.username}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.username}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicEmail">
                          <Form.Control
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.email && !!errors.email}
                            isValid={touched.email && !errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicFirstName"
                        >
                          <Form.Control
                            name="firstName"
                            placeholder="First Name"
                            value={values.firstName}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={touched.firstName && !!errors.firstName}
                            isValid={touched.firstName && !errors.firstName}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.firstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicLastName"
                        >
                          <Form.Control
                            name="lastName"
                            placeholder="Last Name"
                            value={values.lastName}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={touched.lastName && !!errors.lastName}
                            isValid={touched.lastName && !errors.lastName}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.lastName}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                          className="mb-3"
                          controlId="formBasicPassword"
                        >
                          <Form.Control
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.password && !!errors.password}
                            isValid={touched.password && !errors.password}
                          />
                          <Form.Control.Feedback>
                            Looks good!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicPasswordConfirm"
                        >
                          <Form.Control
                            type="password"
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touched.confirmPassword &&
                              !!errors.confirmPassword
                            }
                            isValid={
                              touched.confirmPassword && !errors.confirmPassword
                            }
                          />
                          <Form.Control.Feedback>
                            Passwords match!
                          </Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmPassword}
                          </Form.Control.Feedback>
                        </Form.Group>
                        {(partner || isOpenSignup) && (
                          <Form.Group
                            className="mb-3"
                            // @ts-expect-error react-bootstrap FormGroup has no
                            // `sm` prop; inert layout attribute, preserved as-is.
                            sm={4}
                            controlId="formBasicReferralCode"
                          >
                            <div className="referralCode">
                              <Form.Control
                                name="referralCode"
                                placeholder={
                                  partner
                                    ? "Referral Code"
                                    : "Referral Code (Optional)"
                                }
                                value={values.referralCode}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                disabled={lockReferralCode}
                                isInvalid={
                                  touched.referralCode && !!errors.referralCode
                                }
                                isValid={
                                  touched.referralCode && !errors.referralCode
                                }
                              />
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.referralCode}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                      </article>
                    </section>
                    <section className="form-sec-2col">
                      <article className="col-form">
                        <p
                          style={{
                            fontStyle: "italic",
                            fontSize: 14,
                            paddingTop: 10,
                            paddingLeft: 4,
                          }}
                        >
                          By pressing the Continue button to create your account
                          you are certifying that you have read and agree to our{" "}
                          <a
                            href="https://iconsavingsplan.com/terms"
                            className="modal-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#01a3b0",
                            }}
                          >
                            Terms of Service
                          </a>{" "}
                          and{" "}
                          <a
                            href="https://iconsavingsplan.com/privacy"
                            className="modal-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#01a3b0",
                            }}
                          >
                            Privacy Policy
                          </a>
                          .
                        </p>
                        {this.props.error && (
                          <Alert type="error" msg={this.props.error} />
                        )}
                        <div className="submit-row">
                          <Button
                            name="submit"
                            withArrow={true}
                            loading={this.props.isFetching}
                          />
                        </div>
                      </article>
                    </section>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerSignup));
